import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout/PageHeader';
import { MotionConfig } from 'framer-motion';
import { Download, Plus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import type { Lead } from '@/types/api';
import { useCopilotCommand, CopilotCommand } from '@/features/copilot/copilotBus';
import { useLeads, useLeadActions, useLeadStats } from './hooks/useLeads';
import { LeadsStatsBar } from './components/LeadsStatsBar';
import { LeadsToolbar } from './components/LeadsToolbar';
import { BulkActionBar } from './components/BulkActionBar';
import { LeadsTable } from './components/LeadsTable';
import { EmptyLeadsState } from './components/EmptyLeadsState';
import { LeadsSkeleton } from './components/LeadsSkeleton';

const LeadsPage = () => {
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const { data: leadsData, isLoading } = useLeads({
    search: searchTerm,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    platform: platformFilter !== 'all' ? platformFilter : undefined,
    priority: priorityFilter !== 'all' ? priorityFilter : undefined,
    page: currentPage,
    limit: 20,
  });

  const { data: stats } = useLeadStats();
  const { updateLead, deleteLead, bulkUpdateLeads } = useLeadActions();

  // Annotated: `leadsData?.leads || []` is `Lead[] | never[]`, and TS 4.9 cannot type the callback
  // parameters of .map on a union of array types.
  const leads = useMemo<Lead[]>(() => leadsData?.leads ?? [], [leadsData]);
  const totalPages = leadsData?.pages || 1;
  const hasActiveFilters =
    statusFilter !== 'all' || platformFilter !== 'all' || priorityFilter !== 'all' || searchTerm.trim() !== '';
  const listKey = `${searchTerm}|${statusFilter}|${platformFilter}|${priorityFilter}|${currentPage}`;

  // Build a CSV from the currently visible leads and trigger a browser download.
  const exportLeads = () => {
    if (leads.length === 0) {
      toast('No leads to export');
      return;
    }

    const columns: { header: string; value: (lead: Lead) => string }[] = [
      { header: 'Name', value: (lead) => lead.customerName },
      { header: 'Message', value: (lead) => lead.message },
      { header: 'Channel', value: (lead) => lead.platform },
      { header: 'Source', value: (lead) => lead.source },
      { header: 'Status', value: (lead) => lead.status },
      { header: 'Priority', value: (lead) => lead.priority },
      { header: 'Created', value: (lead) => lead.createdAt },
      { header: 'Updated', value: (lead) => lead.updatedAt },
    ];

    // Wrap every field in quotes and escape embedded quotes so commas/newlines are safe.
    const escape = (value: string) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = [
      columns.map((column) => escape(column.header)).join(','),
      ...leads.map((lead) => columns.map((column) => escape(column.value(lead))).join(',')),
    ];
    const csv = rows.join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${leads.length} leads`);
  };

  // Let the Copilot trigger the same export after it navigates to this page.
  useCopilotCommand(CopilotCommand.LeadsExport, exportLeads);

  if (isLoading) {
    return <LeadsSkeleton />;
  }

  const handleClearFilters = () => {
    setStatusFilter('all');
    setPlatformFilter('all');
    setPriorityFilter('all');
    setSearchTerm('');
  };

  const handleToggleAll = (checked: boolean) => setSelectedLeads(checked ? leads.map((lead) => lead.id) : []);

  const handleToggleOne = (id: string, checked: boolean) =>
    setSelectedLeads((prev) => (checked ? [...prev, id] : prev.filter((leadId) => leadId !== id)));

  return (
    // reducedMotion="user" makes every motion.* element below skip transform/layout
    // animation automatically when the OS "reduce motion" preference is on.
    <MotionConfig reducedMotion="user">
      <div className="min-h-full bg-transparent p-4 md:p-6">
        <div className="mx-auto max-w-7xl space-y-4">
          {/* Summary stats */}
          <LeadsStatsBar stats={stats} />

          {/* Search + filters */}
          <LeadsToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            platformFilter={platformFilter}
            onPlatformChange={setPlatformFilter}
            priorityFilter={priorityFilter}
            onPriorityChange={setPriorityFilter}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={handleClearFilters}
          />

          {/* Bulk actions — collapses in/out as selection changes */}
          <BulkActionBar
            count={selectedLeads.length}
            onMarkContacted={() =>
              bulkUpdateLeads.mutate({ leadIds: selectedLeads, updates: { status: 'contacted' } })
            }
            onSetHighPriority={() =>
              bulkUpdateLeads.mutate({ leadIds: selectedLeads, updates: { priority: 'high' } })
            }
            onClear={() => setSelectedLeads([])}
          />

          {/* Leads list */}
          <Card className="max-w-none overflow-hidden p-0">
            {leads.length === 0 ? (
              <EmptyLeadsState hasActiveFilters={hasActiveFilters} onClearFilters={handleClearFilters} />
            ) : (
              <LeadsTable
                leads={leads}
                selectedIds={selectedLeads}
                allSelected={selectedLeads.length > 0 && selectedLeads.length === leads.length}
                onToggleAll={handleToggleAll}
                onToggleOne={handleToggleOne}
                onUpdate={(id, updates) => updateLead.mutate({ id, updates })}
                onDelete={(id) => deleteLead.mutate(id)}
                listKey={listKey}
              />
            )}
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <p>
                Page {currentPage} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((page) => page - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((page) => page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MotionConfig>
  );
};

export default LeadsPage;
